import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Loader } from 'lucide-react';
import { InsuranceService } from '../../services/insuranceService';
import { filterOptions } from '../shared/FilterOptions';
import { useClinicianStore } from '../../store/clinicianStore';

interface InsuranceVerificationProps {
  groupId: string;
}

export const InsuranceVerification: React.FC<InsuranceVerificationProps> = ({ groupId }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    memberId: '',
    groupNumber: '',
    insuranceProvider: '',
    clinicianId: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [verificationResult, setVerificationResult] = useState<any>();

  const clinicians = useClinicianStore(state => 
    state.clinicians.filter(c => c.groupId === groupId)
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(undefined);
    setVerificationResult(undefined);

    try {
      const insuranceService = InsuranceService.getInstance();
      const result = await insuranceService.verifyInsurance({
        patientInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          memberId: formData.memberId,
          groupNumber: formData.groupNumber,
        },
        insuranceProvider: formData.insuranceProvider,
        clinicianId: formData.clinicianId || undefined,
      });

      setVerificationResult(result);
    } catch (err) {
      setError('Failed to verify insurance. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Insurance Verification</h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              <input
                type="text"
                required
                value={formData.firstName}
                onChange={e => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                required
                value={formData.lastName}
                onChange={e => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <input
                type="date"
                required
                value={formData.dateOfBirth}
                onChange={e => setFormData(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Insurance Provider</label>
              <select
                required
                value={formData.insuranceProvider}
                onChange={e => setFormData(prev => ({ ...prev, insuranceProvider: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              >
                <option value="">Select Provider</option>
                {filterOptions.insuranceProviders.map(provider => (
                  <option key={provider} value={provider}>{provider}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Member ID</label>
              <input
                type="text"
                required
                value={formData.memberId}
                onChange={e => setFormData(prev => ({ ...prev, memberId: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Group Number</label>
              <input
                type="text"
                value={formData.groupNumber}
                onChange={e => setFormData(prev => ({ ...prev, groupNumber: e.target.value }))}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Clinician</label>
            <select
              value={formData.clinicianId}
              onChange={e => setFormData(prev => ({ ...prev, clinicianId: e.target.value }))}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
            >
              <option value="">Select Clinician (Optional)</option>
              {clinicians.map(clinician => (
                <option key={clinician.id} value={clinician.id}>{clinician.name}</option>
              ))}
            </select>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader className="animate-spin h-5 w-5 mr-2" />
                Verifying...
              </>
            ) : (
              'Verify Insurance'
            )}
          </button>
        </form>

        {verificationResult && (
          <div className="mt-8 space-y-6">
            <div className="rounded-md bg-green-50 p-4">
              <div className="flex">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Insurance Verified</h3>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Benefits Summary</h3>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Network Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verificationResult.benefits.inNetwork ? 'In-Network' : 'Out-of-Network'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Individual Deductible</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    ${verificationResult.benefits.deductible.individual.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Remaining Deductible</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    ${verificationResult.benefits.deductible.remaining.toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Copay</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    ${verificationResult.benefits.copay}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Coinsurance</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verificationResult.benefits.coinsurance}%
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Session Limit</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verificationResult.benefits.sessionLimit} sessions
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Pre-Authorization Required</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verificationResult.benefits.preAuthRequired ? 'Yes' : 'No'}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Effective Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(verificationResult.benefits.effectiveDate).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
              {verificationResult.benefits.notes && (
                <div className="mt-6">
                  <dt className="text-sm font-medium text-gray-500">Additional Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {verificationResult.benefits.notes}
                  </dd>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};